import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import { api, clearTokens, getTokens, setTokens, purgePersistedCache } from "@/lib/api";
import { bootNativeOnce, shutdownNative } from "@/lib/native";
import { clearBiometricCredentials } from "@/lib/biometric";

const AuthContext = createContext(null);

// Persistent auth-shape cache. Holds the last successful bootstrap so
// cold start can paint <500ms instead of waiting on Railway + 3 RTTs.
const PERSIST_KEY = "kinly.auth-bootstrap";

async function loadPersistedAuth() {
  try {
    return (await idbGet(PERSIST_KEY)) || null;
  } catch {
    return null;
  }
}

async function persistAuth(snapshot) {
  try { await idbSet(PERSIST_KEY, snapshot); } catch { /* noop */ }
}

async function clearPersistedAuth() {
  try { await idbDel(PERSIST_KEY); } catch { /* noop */ }
}

export function AuthProvider({ children }) {
  // undefined = checking; null = unauthenticated; object = signed in
  const [user, setUser] = useState(undefined);
  // undefined = unknown (still loading), null = none, object = loaded
  const [family, setFamily] = useState(undefined);
  // Backend-driven invite list. Source of truth for "this email has unfinished business".
  // Survives device hops (open email on phone, sign up on TestFlight) because it's keyed
  // on user.email server-side, not on per-tab sessionStorage.
  const [pendingInvites, setPendingInvites] = useState([]);
  // Shared members roster — sourced once from bootstrap, refreshed on family change.
  // Replaces the 6+ redundant /members fetches that every screen previously made.
  const [members, setMembers] = useState([]);

  const fetchPendingInvites = useCallback(async () => {
    try {
      const { data } = await api.get("/me/pending-invites");
      const invs = Array.isArray(data?.invites) ? data.invites : [];
      setPendingInvites(invs);
      return invs;
    } catch {
      setPendingInvites([]);
      return [];
    }
  }, []);

  /**
   * Cold-start bootstrap — single round-trip replaces /auth/me + /families/{id}
   * + /me/pending-invites + /members. Uses the new /api/auth/bootstrap composite
   * endpoint. Falls back to legacy sequential calls if bootstrap is unavailable
   * (e.g. old backend on Railway during a rolling deploy).
   */
  const fetchBootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/bootstrap");
      const fam = data.family || null;
      const mem = Array.isArray(data.members) ? data.members : [];
      const inv = Array.isArray(data.pending_invites) ? data.pending_invites : [];
      setFamily(fam);
      setMembers(mem);
      setPendingInvites(inv);
      setUser(data.user);
      // Fire-and-forget — do NOT block the splash on native init (FCM + RC).
      bootNativeOnce(data.user).catch(() => { /* non-fatal */ });
      // Persist this exact snapshot for instant cold-start next time.
      persistAuth({
        user: data.user, family: fam, members: mem,
        pending_invites: inv, ts: Date.now(),
      });
      return data.user;
    } catch (e) {
      // 404 on bootstrap (older backend) → fallback to legacy serial fetch
      if (e?.response?.status === 404) {
        return legacyFetchMe();
      }
      // Anything else: clear auth so we route to /signin instead of hanging
      // on a hydrated stale snapshot from before a password change/etc.
      setFamily(null);
      setUser(null);
      setMembers([]);
      setPendingInvites([]);
      clearPersistedAuth();
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Pre-bootstrap fallback — kept for graceful degradation against an
   *  older backend without /auth/bootstrap. Same behaviour as before. */
  const legacyFetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      let fam = null;
      if (data.primary_family_id) {
        try {
          const r = await api.get(`/families/${data.primary_family_id}`);
          fam = r.data;
        } catch { fam = null; }
      }
      const invs = await fetchPendingInvites();
      let mem = [];
      if (fam?.id) {
        try {
          const r = await api.get(`/families/${fam.id}/members`);
          mem = r.data || [];
        } catch { mem = []; }
      }
      setFamily(fam);
      setMembers(mem);
      setUser(data);
      bootNativeOnce(data).catch(() => { /* non-fatal */ });
      // Batch 41 — silent background refresh of any stale template subs
      if (fam?.id) {
        import("@/lib/templateAutoRefresh").then(({ maybeRefreshSubscriptions }) => {
          maybeRefreshSubscriptions(fam.id);
        }).catch(() => {});
      }
      persistAuth({ user: data, family: fam, members: mem, pending_invites: invs, ts: Date.now() });
      return data;
    } catch {
      setFamily(null);
      setUser(null);
      setMembers([]);
      setPendingInvites([]);
      clearPersistedAuth();
      return null;
    }
  }, [fetchPendingInvites]);

  /**
   * Public refresh — used by callers that just mutated something
   * (createFamily, member added, etc). Always uses the bootstrap path.
   */
  const fetchMe = useCallback(async () => {
    return fetchBootstrap();
  }, [fetchBootstrap]);

  // Cold-start: if we have a persisted snapshot, paint INSTANTLY with
  // last-known data, then refresh in the background. Splash duration
  // drops from 20s on Railway cold to <50ms on warm reopen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getTokens()) {
        setFamily(null);
        setUser(null);
        return;
      }
      const snap = await loadPersistedAuth();
      if (snap && !cancelled) {
        // Instant paint from cache — Splash drops immediately.
        setUser(snap.user);
        setFamily(snap.family);
        setMembers(snap.members || []);
        setPendingInvites(snap.pending_invites || []);
      }
      // Always refresh in the background, with the cache showing.
      if (!cancelled) fetchBootstrap();
    })();
    return () => { cancelled = true; };
  }, [fetchBootstrap]);

  const signup = async ({ email, password, display_name, nickname }) => {
    const { data } = await api.post("/auth/signup", { email, password, display_name, nickname });
    setTokens(data);
    await fetchBootstrap();
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data);
    // Update the persisted snapshot so cold-starts show the new name
    const snap = await loadPersistedAuth();
    if (snap) persistAuth({ ...snap, user: data });
    return data;
  };

  const login = async ({ email, password }) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data);
    await fetchBootstrap();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    try { await shutdownNative(); } catch (_e) { /* ignore */ }
    try { await purgePersistedCache(); } catch (_e) { /* ignore */ }
    await clearPersistedAuth();
    clearTokens();
    // Wipe any saved biometric sign-in so a different user on this device
    // can't unlock the previous account.
    clearBiometricCredentials();
    setFamily(null);
    setUser(null);
    setMembers([]);
    setPendingInvites([]);
  };

  const createFamily = async (payload) => {
    const { data } = await api.post("/families", payload);
    setFamily(data);
    await fetchBootstrap();
    return data;
  };

  /** Public hook for screens that need a fresh members roster (e.g.
   *  after inviting someone). Updates the shared context state. */
  const refreshMembers = useCallback(async () => {
    if (!family?.id) return [];
    try {
      const { data } = await api.get(`/families/${family.id}/members`);
      setMembers(data || []);
      const snap = await loadPersistedAuth();
      if (snap) persistAuth({ ...snap, members: data || [] });
      return data || [];
    } catch {
      return members;
    }
  }, [family?.id, members]);

  return (
    <AuthContext.Provider
      value={{
        user,
        family,
        members,
        pendingInvites,
        signup,
        login,
        logout,
        createFamily,
        updateProfile,
        refresh: fetchMe,
        refreshMembers,
        refreshPendingInvites: fetchPendingInvites,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
